<template>
  <div :id="uppyId">
    <div class="DashboardContainer"></div>
  </div>
</template>

<script>
  import {mapState} from "vuex";

  const Uppy = require('@uppy/core');
  const XHRUpload = require('@uppy/xhr-upload');
  const Dashboard = require('@uppy/dashboard');

  // And their styles (for UI plugins)
  require('@uppy/core/dist/style.css');
  require('@uppy/dashboard/dist/style.css');

  const normalizeLocale = (locale) => {
    if (locale.includes('_')) {
      return locale;
    }

    // If contains dash, replace with underscore
    if (locale.includes('-')) {
      return locale.replace('-', '_');
    }

    // If only language code (e.g. 'pl'), add uppercase version
    return `${locale}_${locale.toUpperCase()}`;
  };

  const loadLocale = (locale) => {
    const fileName = normalizeLocale(locale);
    return import(`./${fileName}.js`);
  };

  export default {

    data() {
      return {
        endpoint: 'attachments/upload',
        modelClass: 'uppy',
        modelId: 'uppyId',
        collection: 'thumbnails',
      }
    },

    computed: {
      ...mapState({
        token: state => state.auth.token,
        filesLeft: state => state.settings.attachments.maxFiles - state.attachments.attachments.length,
        maxFileSize: state => state.settings.attachments.maxFileSize,
      }),
      uppyId() {
        return this.modelClass + '-' + this.modelId + '-' + this.collection;
      }
    },

    async mounted() {
      const currentLocale = localStorage.getItem('locale') ? localStorage.getItem('locale') : this.$i18n.locale;

      const uppyLocaleModule = await loadLocale(currentLocale);
      const uppyLocale = uppyLocaleModule.default;

      const uppy = Uppy({
        id: this.uppyId,
        autoProceed: false,
        debug: false,
        locale: uppyLocale,
        restrictions: {
          maxFileSize: this.maxFileSize,
          maxNumberOfFiles: this.filesLeft,
          allowedFileTypes: [
            'image/jpeg',
            'image/gif',
            'image/png',
            'application/pdf',
            'application/xml', // xml
            'text/xml', // xml
            'text/csv', // csv
            'application/vnd.ms-excel', // xls
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // xlsx
            'application/x-vnd.oasis.opendocument.spreadsheet' // ods
          ]
        },
        meta: {
          modelClass: this.modelClass,
          modelId: this.modelId,
          collection: this.collection
        },
        onBeforeFileAdded: () => {
          Promise.resolve()
        },
        onBeforeUpload: () => {
          Promise.resolve()
        },
      });

      uppy.use(Dashboard, {
        inline: true,
        proudlyDisplayPoweredByUppy: false,
        target: '.DashboardContainer',
        replaceTargetContent: true,
        note: this.$t('Images, PDF, XML, XLSX, XLS and CSV.'),
        maxHeight: 500,
        // The Dashboard plugin has its own default strings; pass the loaded locale so
        // dropPasteFiles (and the rest) are localized here too, not just on Core.
        locale: uppyLocale,
      });
      uppy.use(XHRUpload, {
        endpoint: process.env.VUE_APP_API_URI + this.endpoint,
        formData: false,
        limit: 1,
        headers: {
          'Authorization': `Bearer ${this.token}`,
          'Content-Type': 'application/octet-stream'
        },
      });

      uppy.on('file-added', (file) => {
        uppy.setFileState(file.id, {
          xhrUpload: {
            headers: {
              'Content-Disposition': `attachment; filename*=UTF-8''${encodeURIComponent(file.name)}`
            }
          }
        });
      });

      uppy.on('upload-success', (file, response) => {
        this.$emit('upload-success', response)
      });

      uppy.on('upload-error', (file, error, response) => {
        if (400 === response.status && response.body.hasOwnProperty('message')) {
          uppy.info({ message: response.body.message }, 'error', 5000);
        }
      });

      uppy.on('complete', (result) => {
        if (result.failed.length === 0) {
          this.$emit('complete-success')
        }
      });

      uppy.run();
    },

    methods: {},
  }
</script>

<style>
  .uppy-Dashboard-inner {
    border: unset;
    background-color: #fff;
    border-radius: 0 0 .3rem .3rem;
  }
</style>
